type SentryFrame = {
  filename?: string
  abs_path?: string
  function?: string
}

type SentryExceptionValue = {
  type?: string
  value?: string
  stacktrace?: { frames?: SentryFrame[] }
}

export type ClientSentryEvent = {
  message?: string
  exception?: { values?: SentryExceptionValue[] }
}

type SentryEventHint = {
  originalException?: unknown
}

const APP_BUG_PATTERN =
  /Maximum call stack size exceeded|dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading CSS chunk|Loading chunk [\d]* failed|not valid as a React child/i

const THIRD_PARTY_FRAME = /thostedf\.js|widget_app|userway|_0x4d8d/i

const MESSAGE_NOISE = [
  /Java object is gone/i,
  /webkit\.messageHandlers/i,
  /Failed to get ServiceWorkerRegistration/i,
  /denied permission to use Service Worker/i,
  /InvalidStateError[\s\S]{0,120}ServiceWorker/i,
  /NotSupportedError[\s\S]{0,120}ServiceWorker/i,
  /reading ['"]localStorage['"]/i,
  /localStorage['"] property from ['"]Window['"]/i,
  /Access is denied for this document/i,
  /\bfbq is not defined\b|Can't find variable: fbq/i,
  /\bzp_token is not defined\b|Can't find variable: zp_token/i,
  /containerElem\.appendChild/i,
  /iframeId\)\.blur/i,
  /_0x4d8d/,
  /Object Not Found Matching Id/i,
  /Failed to fetch \((?:ad\.doubleclick\.net|(?:www\.)?google\.com|api\.userway\.org)/i,
  /api\.userway\.org/i,
  /blocked a frame with origin/i,
  /cross-origin frame/i,
  /hf-bit-cont/i,
]

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function exceptionValues(event: ClientSentryEvent): SentryExceptionValue[] {
  return event.exception?.values ?? []
}

function eventText(event: ClientSentryEvent): string {
  const parts = exceptionValues(event).map(value => `${value.type ?? ''} ${value.value ?? ''}`)
  return `${parts.join('\n')} ${event.message ?? ''}`
}

function frames(event: ClientSentryEvent): SentryFrame[] {
  return exceptionValues(event).flatMap(value => value.stacktrace?.frames ?? [])
}

function frameText(frame: SentryFrame): string {
  return `${frame.filename ?? ''} ${frame.abs_path ?? ''} ${frame.function ?? ''}`
}

function hasAppStack(event: ClientSentryEvent): boolean {
  return frames(event).some(frame => {
    const file = `${frame.filename ?? ''} ${frame.abs_path ?? ''}`.trim()
    return file.length > 0 && !THIRD_PARTY_FRAME.test(file)
  })
}

function hasThirdPartyFrame(event: ClientSentryEvent): boolean {
  return frames(event).some(frame => THIRD_PARTY_FRAME.test(frameText(frame)))
}

function isCanceledRequest(event: ClientSentryEvent, hint?: SentryEventHint): boolean {
  const text = eventText(event)
  const original = hint?.originalException
  const code = isRecord(original) && typeof original.code === 'string' ? original.code : ''
  if (code === 'ERR_CANCELED') return true
  if (/\bRequest aborted\b/i.test(text)) return true
  if (/\bERR_CANCELED\b/.test(text)) return true
  return false
}

function isStacklessRejection(event: ClientSentryEvent): boolean {
  if (hasAppStack(event)) return false
  const text = eventText(event)
  if (/CustomEvent/i.test(text) && /unhandledrejection/i.test(text)) return true
  if (/\[object has no keys\]/i.test(text)) return true
  if (/Non-Error promise rejection captured with value:\s*\{\s*\}/i.test(text)) return true
  return false
}

export function shouldDropClientSentryEvent(event: ClientSentryEvent, hint?: SentryEventHint): boolean {
  const text = eventText(event)
  if (APP_BUG_PATTERN.test(text)) return false
  if (hasThirdPartyFrame(event)) return true
  if (MESSAGE_NOISE.some(pattern => pattern.test(text))) return true
  if (isCanceledRequest(event, hint)) return true
  if (isStacklessRejection(event)) return true
  return false
}

export function beforeSendSentryEvent<T extends ClientSentryEvent>(event: T, hint?: SentryEventHint): T | null {
  if (shouldDropClientSentryEvent(event, hint)) return null
  return event
}
